import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { Button, TableRowActionMenu } from "../components/ui";
import { deleteTemplate } from "../store/features/ownerActions";
import { ownerSelector, templateSelector, type RootState } from "../store/store";
import {
  getSequencePathLabel,
  getSequenceStepsFromTemplate,
  isSequenceTemplate,
} from "../utils/sequenceTemplate";

const Templates = () => {
  const navigate = useNavigate();
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const { templates } = useSelector(templateSelector);
  const { currentWorkspace, currentUser } = useSelector(ownerSelector);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const workspaceId = useMemo(() => {
    if (typeof currentWorkspace !== "string") return currentWorkspace?._id as string | undefined;
    try {
      return JSON.parse(currentWorkspace || "null")?._id as string | undefined;
    } catch {
      return undefined;
    }
  }, [currentWorkspace]);

  const userId = currentUser?.userId as string | undefined;
  const sequenceTemplates = (Array.isArray(templates) ? templates : []).filter(isSequenceTemplate);

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setPendingDelete(null);
  };

  const handleDelete = async () => {
    if (!pendingDelete?.id) return;
    setIsDeleting(true);
    const res = await dispatch(
      deleteTemplate({
        templateIds: [pendingDelete.id],
        userId,
        workspaceId,
      })
    );
    setIsDeleting(false);
    if (res.meta.requestStatus === "fulfilled") {
      toast.success("Template deleted");
      setPendingDelete(null);
    } else {
      toast.error("Failed to delete template");
    }
  };

  return (
    <section className="relative noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">
            Sequence templates
          </h1>
          <p className="mt-1 text-sm text-muted">
            Start campaigns from proven multi-step sequences, then clone and tailor them to your goal.
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="primary"
            size="sm"
            className="h-10 px-3.5 font-semibold text-dark-blue"
            onClick={() => navigate("/templates/create")}
          >
            + Create sequence template
          </Button>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px]">
          <p className="text-sm font-semibold text-dark-blue">Sequence templates, not message templates</p>
          <p className="text-xs text-notice-text">
            Templates contain a reusable action flow. Message content is configured on each action in the flow.
          </p>
        </div>

        <h2 className="mb-3 text-[15px] font-semibold tracking-tight text-dark-blue">
          Pre-built templates
        </h2>
        {sequenceTemplates.length > 0 ? (
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
            {sequenceTemplates.map((template: any) => {
              const name = String(template?.name || template?.templateName || "").trim();
              const path = getSequencePathLabel(getSequenceStepsFromTemplate(template));
              const id = String(template?._id || "");
              return (
                <article
                  key={id || name}
                  className="flex min-h-[170px] cursor-pointer flex-col rounded-[10px] border border-line bg-white p-[17px] hover:border-mainColor hover:shadow-sm"
                  onClick={() => id && navigate(`/templates/${id}`)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="self-start rounded-xl bg-mini-avatar px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-mini-avatar-fg">
                      Sequence flow
                    </span>
                    {id ? (
                      <div
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <TableRowActionMenu
                          onDelete={() =>
                            setPendingDelete({ id, name: name || "Untitled template" })
                          }
                          deleteLabel="Delete"
                        />
                      </div>
                    ) : null}
                  </div>
                  <h2 className="mt-3.5 text-[15px] font-semibold tracking-tight text-dark-blue">
                    {name || "Untitled template"}
                  </h2>
                  <p className="mt-1 text-[11px] text-muted">{path}</p>
                  <footer className="mt-auto flex items-center justify-end pt-4">
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-muted hover:text-dark-blue"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (id) navigate(`/templates/${id}`);
                      }}
                    >
                      Open flow
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-[10px] border border-line bg-white px-6 py-10 text-center">
            <p className="text-sm text-muted">No sequence templates yet.</p>
          </div>
        )}
      </div>

      {pendingDelete && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
              <div
                className="relative w-full max-w-md min-w-0 rounded-lg bg-white p-6 text-left shadow-xl"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  aria-label="Close"
                  className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-paper hover:text-dark-blue"
                  onClick={closeDeleteConfirm}
                >
                  ×
                </button>
                <p className="pr-8 text-left text-lg font-semibold text-dark-blue">
                  Delete this template?
                </p>
                <p className="mt-3 text-left text-sm text-muted">
                  <span className="font-medium text-dark-blue">{pendingDelete.name}</span> will be
                  removed from your workspace. This cannot be undone.
                </p>
                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    isDisabled={isDeleting}
                    onClick={closeDeleteConfirm}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="btn-red"
                    size="sm"
                    isDisabled={isDeleting}
                    isLoading={isDeleting}
                    onClick={() => void handleDelete()}
                  >
                    {isDeleting ? "Deleting..." : "Delete template"}
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
};

export default Templates;
